import { useState } from 'react'
import { toast } from 'sonner'
import { Panel } from '@/components/redesign/bits'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { errorMessage } from '@/lib/api/errors'
import { formatRelative } from '@/lib/format'
import { type LearnedLine, type ScriptHost, useDecideHost, useDecideLine, useSecurityFeed } from './api'

const lineVerdicts = { review: 'To review', malicious: 'Malicious', safe: 'Safe' } as const

const hostVerdicts = { review: 'To review', bad: 'Bad', known: 'Known' } as const

/**
 * Lines added to changed files on connected websites (plugin 2.7.0's "Changed since the last
 * scan" and dashboard edits): risky ones, and any line added on several websites, wait here.
 * Malicious ones become a pattern every website's next scan uses; safe ones stop being reported.
 */
export function LearnedLinesCard() {
  const feed = useSecurityFeed()
  const decide = useDecideLine()

  const mark = (line: LearnedLine, verdict: 'malicious' | 'safe') =>
    decide.mutate(
      { fingerprint: line.fingerprint, verdict },
      {
        onSuccess: () =>
          toast.success(
            verdict === 'malicious'
              ? 'Marked as malicious: every website’s next scan looks for it.'
              : 'Marked as safe: changes that only add it aren’t reported.',
          ),
        onError: (error) => toast.error(errorMessage(error)),
      },
    )

  return (
    <Panel
      title="Lines added to changed files"
      sub={
        feed.data
          ? `From “Changed since the last scan” and dashboard edits (plugin 2.7.0+). A line that loads a script, prints into every page or hides code waits here from one website; any other line once it’s added on ${feed.data.meta.line_sites} or more workspaces’ websites. Only the line is kept, never the file or which websites.`
          : 'Lines added to changed files on connected websites.'
      }
    >
      {feed.isPending ? (
        <Skeleton className="h-24 rounded-md" />
      ) : feed.isError ? (
        <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
      ) : feed.data.lines.length === 0 ? (
        <p className="empty-line">Nothing to learn yet: no changed file added a line worth reviewing.</p>
      ) : (
        <div className="tw" style={{ border: 0 }}>
          <table className="t" style={{ minWidth: 760 }}>
            <caption className="sr-only">Lines added to changed files</caption>
            <thead>
              <tr>
                <th>Line</th>
                <th>From</th>
                <th className="r">Websites</th>
                <th>Last seen</th>
                <th>Verdict</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {feed.data.lines.map((l) => (
                <tr key={l.fingerprint}>
                  <td style={{ wordBreak: 'break-all' }}>
                    <code>{l.line}</code>
                    {l.risky && <div className="m">Loads, prints or hides code</div>}
                  </td>
                  <td className="m">{l.source === 'file_edit' ? 'A dashboard edit' : 'A changed file'}</td>
                  <td className="r num">{l.sites.toLocaleString()}</td>
                  <td className="m" style={{ whiteSpace: 'nowrap' }}>
                    {formatRelative(l.last_seen_at)}
                  </td>
                  <td>
                    <b>{lineVerdicts[l.verdict]}</b>
                    {l.decided_by && <div className="m">by {l.decided_by}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {l.verdict !== 'malicious' && (
                      <button
                        type="button"
                        className="btn sm"
                        disabled={decide.isPending}
                        onClick={() => mark(l, 'malicious')}
                      >
                        Malicious
                      </button>
                    )}{' '}
                    {l.verdict !== 'safe' && (
                      <button
                        type="button"
                        className="btn sm ghost"
                        disabled={decide.isPending}
                        onClick={() => mark(l, 'safe')}
                      >
                        Safe
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

/** Why a host is listed, from its checks. */
function hostReasons(h: ScriptHost): string {
  const reasons = []
  if (h.web_risk === 'flagged')
    reasons.push(`Google lists it as unsafe (${h.web_risk_threats.join(', ').toLowerCase()})`)
  if (h.registered_at) reasons.push(`registered ${h.registered_at}`)
  if (h.hides) reasons.push(h.hides_detail ?? 'hides what it does')
  return reasons.join(' · ')
}

/**
 * Hosts WordPress sites load scripts from (plugin 2.7.0+). New, hiding or unsafe ones wait here;
 * hosts on many workspaces' websites that are old and clean become known by themselves. Bad ones
 * are flagged on every website that loads them, at once and through the feed.
 */
export function ScriptHostsCard() {
  const feed = useSecurityFeed()
  const decide = useDecideHost()
  const [names, setNames] = useState<Record<string, string>>({})

  const mark = (host: ScriptHost, verdict: 'known' | 'bad') => {
    const label = names[host.host]?.trim()
    decide.mutate(
      { host: host.host, verdict, ...(verdict === 'bad' && label ? { name: label } : {}) },
      {
        onSuccess: () =>
          toast.success(
            verdict === 'bad'
              ? 'Marked as bad: every website that loads it is told.'
              : 'Marked as known: it isn’t checked or reported again.',
          ),
        onError: (error) => toast.error(errorMessage(error)),
      },
    )
  }

  return (
    <Panel
      title="Script hosts"
      sub={
        feed.data
          ? `Websites the connected sites’ pages load scripts from (plugin 2.7.0+), other than well-known services. Each is checked once a week: how new its domain is, whether it hides what it does, and Google’s Web Risk list. Hosts on ${feed.data.meta.known_sites} or more workspaces’ websites, registered over a year ago and clean, become known by themselves.`
          : 'Websites the connected sites load scripts from.'
      }
    >
      {feed.isPending ? (
        <Skeleton className="h-24 rounded-md" />
      ) : feed.isError ? (
        <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
      ) : feed.data.hosts.length === 0 ? (
        <p className="empty-line">
          Nothing to review: no connected website loads a script from a new, hiding or unsafe host.
        </p>
      ) : (
        <div className="tw" style={{ border: 0 }}>
          <table className="t" style={{ minWidth: 760 }}>
            <caption className="sr-only">Script hosts</caption>
            <thead>
              <tr>
                <th>Host</th>
                <th className="r">Websites</th>
                <th>Last seen</th>
                <th>Verdict</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {feed.data.hosts.map((h) => (
                <tr key={h.host}>
                  <td style={{ wordBreak: 'break-all' }}>
                    <b>{h.host}</b>
                    {hostReasons(h) && <div className="m">{hostReasons(h)}</div>}
                    {h.name && <div className="m">{h.name}</div>}
                  </td>
                  <td className="r num">{h.sites.toLocaleString()}</td>
                  <td className="m" style={{ whiteSpace: 'nowrap' }}>
                    {formatRelative(h.last_seen_at)}
                  </td>
                  <td>
                    <b>{hostVerdicts[h.verdict]}</b>
                    {h.decided_by && <div className="m">by {h.decided_by}</div>}
                    {h.source === 'auto' && <div className="m">learned by itself</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {h.verdict !== 'bad' && (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <Input
                          aria-label={`What ${h.host} is`}
                          placeholder="What it is"
                          value={names[h.host] ?? ''}
                          onChange={(e) => setNames((n) => ({ ...n, [h.host]: e.target.value }))}
                          style={{ width: 140 }}
                        />
                        <button
                          type="button"
                          className="btn sm"
                          disabled={decide.isPending}
                          onClick={() => mark(h, 'bad')}
                        >
                          Bad
                        </button>
                      </span>
                    )}{' '}
                    {h.verdict !== 'known' && (
                      <button
                        type="button"
                        className="btn sm ghost"
                        disabled={decide.isPending}
                        onClick={() => mark(h, 'known')}
                      >
                        Known
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
